/**
 * Home — what needs me, what we are doing, and what moved (roadmap E-7;
 * docs/9-exploration/PRD-personal-and-team-questions.md; owner rulings
 * 2026-10-16: three columns, the plan as the centerpiece, quiet sections say
 * why they are quiet, your own items marked in place).
 *
 * One page, one code path. The experience profile chooses the sections:
 *
 *   guided  (bare, at `/`)                   full  (in the app shell)
 *   Orientation and the one-line answer      Orientation and the one-line answer
 *   Your turn: the one thing                 Your turn: the one thing, what else, what I wait on
 *                                            Coming up (sessions I am in, reviews of my decisions)
 *                                            Delegated to me / You delegated (decision rights, 980)
 *   The plan (its top five)                  The plan: in force, being made, or none
 *                                            What moved (changes that reached me, what we decided)
 *                                            Needs fixing (only for whoever can fix it)
 *   The one way out
 *
 * Your turn, the plan and what moved are always drawn; when quiet they say so.
 * Coming up, the two delegation panels and needs fixing are drawn only when
 * they have something to say (build contract §2.8). On a wide screen the page is three columns — you, the
 * plan, what moved; on a laptop two; on a phone one, in that order.
 *
 * The page is a view over rows and RPCs the stores already hold (Doc 26 rule 7,
 * Doc 31 first law): the engine decides what is needed, useHomePlan reads the
 * plan of record, and lib/ phrases it.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useResponsibilityStore } from '../../stores/prioritization/responsibilityStore';
import { useCatchballLoopStore } from '../../stores/prioritization/catchballLoopStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { useProposalPacketStore } from '../../stores/prioritization/proposalPacketStore';
import { useDashboardStats } from '../../components/dashboard/useDashboardStats';
import NoWorkspaceState from '../../components/dashboard/NoWorkspaceState';
import { getResponsibilityNavigationHref } from '../../lib/responsibilityLinks';
import { loadNotificationsForUser, markNotificationsSeenInSupabase, type StoredNotification } from '../../lib/notificationService';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import { listWorkspaceMembers } from '../../lib/workspaceService';
import { setExperienceProfile } from '../../lib/experienceProfile';
import { greetingFor, selectOneThing } from './lib/oneThing';
import { useWaitingProposalLoops } from './useWaitingProposalLoops';
import { teamQuestionsFor } from './lib/teamQuestions';
import { changedIds, changedPoints, comingUp, needsYouLine, needsYouThing, setupSteps, moreThatNeedsMe, waitingLine, waitingOnOthers, whatHasChangedForMe, whatMoved, whatWeDecided, type MovedRow } from './lib/sections';
import { isMine, needsFixing, splitAtLine, summaryLine } from './lib/plan';
import { useHomePlan } from './useHomePlan';
import { PlanSection } from './PlanSection';
import { useMyDelegations } from './useMyDelegations';
import { DelegationList } from './DelegationList';
import { btnPrimary, btnSecondary } from '@/components/mono/primitives';
import { useSpaceStore } from '../../stores/prioritization/spaceStore';
import { AssignmentAskAccept } from '../../components/prioritization/commitments/AssignmentAskAccept';
import { assignmentAcceptOf } from '../../lib/assignmentService';
import { AskButton } from '../../components/ask/AskSheet';
import { CardQuickAction } from '../../components/responsibility/CardQuickAction';
import { quickActionOf } from '../../lib/responsibilityQuickAction';
import { hasPlatformAdminRole, hasTenantAdminRole, hasWorkspaceAdminRole } from '../../lib/authorization/enforce';

export type HomeProfile = 'guided' | 'full';

const EYEBROW = 'font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-ter)] mb-3';
const ROW = 'flex items-start justify-between gap-4 border-t border-[var(--rule)] py-3';
const ROW_LINK = `${ROW} hover:bg-[var(--hover-tint)]`;
const QUIET = 'text-[15px] leading-relaxed text-[var(--text-sec)]';
const MORE_LINK = 'text-sm text-[var(--text-ter)] underline underline-offset-4 hover:text-[var(--text-pri)]';
const CTA =
  'inline-flex min-h-[44px] items-center justify-center bg-[var(--cta)] px-5 font-mono text-sm uppercase tracking-[0.08em] text-[var(--cta-ink)] hover:bg-[var(--cta-hover)]';

function Section({ id, question, className = '', tone, children }: { id: string; question: string; className?: string; tone?: 'fix'; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className={className} data-testid={`home-${id}`}>
      <h2 id={id} className={EYEBROW} style={tone === 'fix' ? { color: 'var(--coral-text)' } : undefined}>
        {question}
      </h2>
      {children}
    </section>
  );
}

export default function HomePage({ profile }: { profile: HomeProfile }) {
  const full = profile === 'full';
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const canStart = useUserStore((s) => s.can('process.create'));
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceResolutionComplete = useTenantStore((s) => s.workspaceResolutionComplete);
  const workspace = useTenantStore((s) => s.getCurrentWorkspace?.()) ?? null;

  const responsibilities = useResponsibilityStore((s) => s.responsibilities);
  const fetchResponsibilities = useResponsibilityStore((s) => s.fetchResponsibilities);
  const responsibilitiesLoading = useResponsibilityStore((s) => s.isLoading);

  // The team answers read the same stores and counts the dashboard did.
  const stats = useDashboardStats();
  const { refreshAllData } = stats;
  const loops = useCatchballLoopStore((s) => s.loops);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const sessions = useSessionStore((s) => s.sessions);
  const proposals = useProposalPacketStore((s) => s.proposals);
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);

  const [notifications, setNotifications] = useState<StoredNotification[]>([]);
  // Whose notices are in `notifications`: until they arrive, "what moved" may
  // not say nothing reached them.
  const [noticesFor, setNoticesFor] = useState<string | null>(null);
  const [helper, setHelper] = useState<{ name: string; email: string } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId && userId !== 'unknown') void fetchResponsibilities(userId);
  }, [userId, fetchResponsibilities]);

  // Full home: the counts must track the database on every visit (build
  // contract §2.1), the way the dashboard refreshed them. Guided home: the one
  // thing and the plan are enough, and both load below.
  useEffect(() => {
    if (!full || !workspaceResolutionComplete || !currentWorkspaceId) return;
    refreshAllData();
    void fetchDecisions();
  }, [full, location.key, currentWorkspaceId, workspaceResolutionComplete, refreshAllData, fetchDecisions]);

  useEffect(() => {
    if (!full || !userId || userId === 'unknown') return;
    let cancelled = false;
    void loadNotificationsForUser(userId).then((rows) => {
      if (cancelled) return;
      setNotifications(rows);
      setNoticesFor(userId);
    });
    return () => {
      cancelled = true;
    };
  }, [full, userId, location.key]);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    let cancelled = false;
    void listWorkspaceMembers(currentWorkspaceId)
      .then((members) => {
        if (cancelled) return;
        const admin = members.find((m) => m.role === 'owner') ?? members.find((m) => m.role === 'admin');
        if (admin?.user) setHelper({ name: admin.user.name || admin.user.email, email: admin.user.email });
      })
      .catch(() => {
        // No helper to name — the lines that use it fall back to "your workspace admin".
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  const roleNames = useUserStore((s) => s.assignedRoleNames);
  const featuredJobRoleSlugs = useUserStore((s) => s.featuredJobRoleSlugs);
  const isAdmin = hasWorkspaceAdminRole(roleNames ?? []) || hasTenantAdminRole(roleNames ?? []) || hasPlatformAdminRole(roleNames ?? []);
  // Which team answers this person's roles care about (R-5, featured roles).
  const teamQ = useMemo(
    () => teamQuestionsFor({ assignedRoleNames: roleNames ?? [], featuredJobRoleSlugs: featuredJobRoleSlugs ?? [], isAdmin }),
    [roleNames, featuredJobRoleSlugs, isAdmin],
  );

  // ── The plan ─────────────────────────────────────────────────────────────
  const cycleLite = useMemo(
    () =>
      cycles.map((c) => ({
        id: c.id,
        name: c.name,
        status: c.status,
        ownerId: c.ownerId ?? null,
        createdBy: c.createdBy ?? null,
        createdAt: c.createdAt ?? null,
        updatedAt: c.updatedAt ?? null,
      })),
    [cycles],
  );
  const cycleMeta = useMemo(() => Object.fromEntries(cycles.map((c) => [c.id, c.metadata])), [cycles]);
  const plan = useHomePlan({
    workspaceId: currentWorkspaceId ?? null,
    viewerId: userId && userId !== 'unknown' ? userId : null,
    isAdmin,
    cycles: cycleLite,
    cycleMeta,
    loops,
    refreshKey: location.key,
  });
  const situationNone = !plan.loading && !plan.makingLoading && !plan.unread && !plan.inForce && !plan.making;
  const offerStart = full && canStart && situationNone;

  // ── Your turn ────────────────────────────────────────────────────────────
  const one = useMemo(() => selectOneThing(responsibilities), [responsibilities]);
  const more = useMemo(() => moreThatNeedsMe(responsibilities), [responsibilities]);
  const waitingProposalIds = useMemo(
    () => responsibilities.filter((c) => c.classification === 'WAITING' && c.subject_type === 'proposal' && c.subject_id).map((c) => c.subject_id as string),
    [responsibilities],
  );
  const waitingLoops = useWaitingProposalLoops(waitingProposalIds);
  const waiting = useMemo(
    () =>
      waitingOnOthers(responsibilities, {
        proposals: [...waitingLoops.proposals, ...proposals],
        loops: [...waitingLoops.loops, ...loops],
        sessions,
      }),
    [responsibilities, proposals, loops, sessions, waitingLoops],
  );
  const spaceType = useSpaceStore((s) => s.getCurrentSpace()?.spaceType ?? null);
  const workspaceType = (workspace as { workspaceType?: string } | null)?.workspaceType ?? null;
  // A workspace with nothing in it yet still says what to do first (the retired
  // page's getting-started list, DEP-030). Waits for the stats so a populated
  // workspace never flashes it. Starting a cycle is offered as "start the plan".
  const setup = useMemo(
    () =>
      stats.isLoading
        ? []
        : setupSteps({
            hasItems: stats.totalItems > 0,
            hasCycles: stats.activeCycles > 0,
            hasSessions: stats.recentSessions.length > 0,
            isPersonal: (spaceType ?? workspaceType) === 'personal',
            isAdmin,
          }).filter((s) => !(offerStart && s.id === 'cycle')),
    [stats.isLoading, stats.totalItems, stats.activeCycles, stats.recentSessions.length, spaceType, workspaceType, isAdmin, offerStart],
  );

  // ── What moved, coming up, needs fixing ──────────────────────────────────
  const changed = useMemo(() => whatHasChangedForMe(notifications), [notifications]);
  const decided = useMemo(() => whatWeDecided(decisions, 5, cycles), [decisions, cycles]);
  const moved = useMemo(() => whatMoved(changed, teamQ.has('decided') ? decided : [], 8), [changed, decided, teamQ]);
  const upcoming = useMemo(() => (userId ? comingUp(sessions, decisions, userId) : []), [sessions, decisions, userId]);

  // ── Delegated decision rights (980) ──────────────────────────────────────
  // A right someone handed this person is something they now decide, so it sits
  // with "your turn"; one they handed to someone else is theirs to extend or revoke.
  const delegations = useMyDelegations(
    full ? currentWorkspaceId ?? null : null,
    userId && userId !== 'unknown' ? userId : null,
    location.key,
  );
  const delegatedToMe = useMemo(() => delegations.rows.filter((d) => d.direction === 'to_me'), [delegations.rows]);
  const delegatedByMe = useMemo(() => delegations.rows.filter((d) => d.direction === 'by_me'), [delegations.rows]);

  const names = useActorDisplayMap([...waiting.map((w) => w.holderId), ...plan.provisional.map((p) => p.ownerId)]);
  const fixes = useMemo(
    () => (userId ? needsFixing(plan.provisional, { id: userId, isAdmin }, names) : []),
    [plan.provisional, userId, isAdmin, names],
  );

  // ── The line under the greeting ──────────────────────────────────────────
  const mineDoing = useMemo(() => {
    if (!plan.inForce) return 0;
    return splitAtLine(plan.inForce.ranks).doing.filter((r) => isMine(plan.itemPeople[r.itemId], userId)).length;
  }, [plan.inForce, plan.itemPeople, userId]);
  // What needs the person, named: the one thing, what else, and starting the plan.
  const needsYou = useMemo(() => {
    const cards = one.kind === 'do' && one.card ? [one.card, ...more.map((m) => m.card)] : [];
    const things = cards.map((c) => needsYouThing(c, getResponsibilityNavigationHref(c)));
    if (offerStart) things.push({ id: 'start-plan', label: 'starting the next plan', href: '/prioritization/cycles/new' });
    return needsYouLine(things);
  }, [one, more, offerStart]);
  const summary = useMemo(() => {
    if (responsibilitiesLoading && responsibilities.length === 0) return null;
    if (plan.loading && !plan.inForce && !plan.making) return null;
    if (!plan.inForce && !plan.making && !plan.unread && plan.makingLoading) return null;
    const split = plan.inForce ? splitAtLine(plan.inForce.ranks) : null;
    return summaryLine({
      unread: plan.unread,
      plan: plan.inForce && split ? { name: plan.inForce.name, doing: split.doing.length, notDoing: split.notDoing.length, mine: mineDoing } : null,
      making: plan.making ? { name: plan.making.cycle.name } : null,
    });
  }, [responsibilitiesLoading, responsibilities.length, plan, mineDoing]);

  // Clearing a row of "what moved" marks every notice it stands for read.
  const clearMoved = async (row: MovedRow & { kind: 'change' }) => {
    const ids = changedIds(row);
    const done = await markNotificationsSeenInSupabase(ids);
    if (done === null) {
      setError('Could not clear that. Try again.');
      return;
    }
    setNotifications((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)));
  };

  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const firstName = (userName || '').trim().split(/\s+/)[0] || '';
  const href = one.card ? getResponsibilityNavigationHref(one.card) : null;
  // An assignment ask (981) carries its act: Accept runs it here; the link opens the commitment.
  const oneAccept = assignmentAcceptOf(one.card);
  const reloadAsks = () => {
    if (userId && userId !== 'unknown') return fetchResponsibilities(userId);
  };

  const showFullApp = async () => {
    if (!currentWorkspaceId || !userId) {
      navigate('/prioritization/dashboard');
      return;
    }
    setLeaving(true);
    try {
      await setExperienceProfile(currentWorkspaceId, userId, 'full');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change that.');
      setLeaving(false);
      return;
    }
    navigate('/prioritization/dashboard');
  };

  // No workspace. Platform and system admins have none by design and keep
  // the page; everyone else gets the honest message.
  if (full && workspaceResolutionComplete && !currentWorkspaceId) {
    const role = useUserStore.getState().currentRole;
    if (role !== 'platform-admin' && role !== 'system-admin') {
      return (
        <NoWorkspaceState
          onSignOut={() => {
            useUserStore.getState().signOut();
            window.location.assign('/auth/login');
          }}
        />
      );
    }
  }

  // ── Sections ─────────────────────────────────────────────────────────────

  const startCard = (
    <div className="flex flex-col gap-2.5 border border-[var(--rule-bright)] bg-[var(--bg-raised)] p-5" data-testid="home-start-plan">
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-ter)]">The one thing</p>
      <h3 className="font-heading text-xl font-semibold leading-snug text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>
        Start the plan
      </h3>
      <p className={QUIET}>
        This makes a cycle as an empty container. Who is in it, and which parts of the organization it passes through, can be decided later.
      </p>
      <Link to="/prioritization/cycles/new" className={`${CTA} mt-1.5 self-start`}>
        Start the plan →
      </Link>
    </div>
  );

  const yourTurn = (
    <Section id="your-turn" question="Your turn" className="order-1 lg:order-none">
      {responsibilitiesLoading && responsibilities.length === 0 ? (
        <p className={QUIET} aria-busy="true">Checking what needs you…</p>
      ) : one.kind === 'do' ? (
        <div className="flex flex-col gap-2.5 border border-[var(--rule-bright)] bg-[var(--bg-raised)] p-5" data-testid="home-one-thing">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-ter)]">The one thing</p>
          <h3 className="font-heading text-xl font-semibold leading-snug text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>
            {one.headline}
          </h3>
          {one.detail && <p className={QUIET}>{one.detail}</p>}
          {one.due && <p className={QUIET}>{one.card?.deadline_means === 'answer' ? 'An answer is' : 'It is'} {one.due}.</p>}
          {one.card && quickActionOf(one.card) ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-4">
              <CardQuickAction card={one.card} className={CTA} onDone={reloadAsks} />
              {href && <Link to={href} className={MORE_LINK}>Open →</Link>}
            </div>
          ) : oneAccept && one.card ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-4">
              <AssignmentAskAccept card={one.card} className={CTA} onDone={reloadAsks} />
              {/* The engine names the way in (1054: a request may live on a cycle, an item, a track…). */}
              {href && (
                <Link to={href} className={MORE_LINK}>
                  {one.card.valid_actions?.find((a) => !a.rpc)?.label ?? 'Open'}
                </Link>
              )}
            </div>
          ) : href ? (
            <Link to={href} className={`${CTA} mt-1.5 self-start`}>
              {one.actionLabel} →
            </Link>
          ) : (
            <p className={QUIET}>This one can't be opened from here yet. {helper ? `Ask ${helper.name}.` : 'Ask your workspace admin.'}</p>
          )}
          {!full && one.moreNeeded > 0 && (
            <p className="mt-2 text-sm text-[var(--text-ter)]">
              {one.moreNeeded === 1 ? 'One more thing' : `${one.moreNeeded} more things`} will follow once this is done.
            </p>
          )}
        </div>
      ) : offerStart ? (
        startCard
      ) : (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="home-one-thing">
          <p className="font-heading text-lg font-semibold text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>{one.headline}</p>
          {one.detail && <p className={`mt-2 ${QUIET}`}>{one.detail}</p>}
        </div>
      )}

      {full && (
        <>
          {one.kind === 'do' && offerStart && <div className="mt-4">{startCard}</div>}
          {more.length > 0 ? (
            <>
              <p className="mb-1 mt-5 text-sm font-semibold text-[var(--text-sec)]">Also needs you</p>
              <ul>
                {more.map(({ card, subject, why, due }) => {
                  const to = getResponsibilityNavigationHref(card);
                  if (quickActionOf(card)) {
                    return (
                      <li key={card.id} className={ROW}>
                        <span className="min-w-0">
                          {to ? (
                            <Link to={to} className="text-[15px] text-[var(--text-pri)] hover:underline">{subject}</Link>
                          ) : (
                            <span className="text-[15px] text-[var(--text-pri)]">{subject}</span>
                          )}
                          {why && <span className="block text-sm text-[var(--text-ter)]">{why}</span>}
                        </span>
                        <CardQuickAction card={card} {...btnPrimary('shrink-0')} onDone={reloadAsks} />
                      </li>
                    );
                  }
                  if (assignmentAcceptOf(card)) {
                    return (
                      <li key={card.id} className={ROW}>
                        <span>
                          {to ? (
                            <Link to={to} className="text-[15px] text-[var(--text-pri)] hover:underline">{subject}</Link>
                          ) : (
                            <span className="text-[15px] text-[var(--text-pri)]">{subject}</span>
                          )}
                          {due && <span className="block text-sm text-[var(--text-ter)]">{due}</span>}
                        </span>
                        <AssignmentAskAccept card={card} {...btnPrimary('shrink-0')} onDone={reloadAsks} />
                      </li>
                    );
                  }
                  const body = (
                    <>
                      <span className="min-w-0">
                        <span className="block text-[15px] text-[var(--text-pri)]">{subject}</span>
                        {why && <span className="block text-sm text-[var(--text-ter)]">{why}</span>}
                        <span className="mt-1 block text-sm font-medium text-[var(--text-pri)]">{card.valid_actions[0]?.label ?? 'Open'} →</span>
                      </span>
                      {due && <span className="shrink-0 text-sm text-[var(--text-ter)]">{due}</span>}
                    </>
                  );
                  return <li key={card.id}>{to ? <Link to={to} className={ROW_LINK}>{body}</Link> : <div className={ROW}>{body}</div>}</li>;
                })}
              </ul>
            </>
          ) : (
            one.kind === 'do' && <p className={`mt-4 ${QUIET}`}>Nothing else needs you.</p>
          )}

          {waiting.length > 0 ? (
            <>
              <p className="mb-1 mt-5 text-sm font-semibold text-[var(--text-sec)]">Waiting on others</p>
              <ul>
                {waiting.map((row) => {
                  const to = getResponsibilityNavigationHref(row.card);
                  const line = waitingLine(row, row.holderId ? names[row.holderId] ?? null : null);
                  const body = (
                    <span>
                      <span className="text-[15px] text-[var(--text-pri)]">{row.subject}</span>
                      {line && <span className="block text-sm text-[var(--text-ter)]">{line}</span>}
                    </span>
                  );
                  return <li key={row.card.id}>{to ? <Link to={to} className={ROW_LINK}>{body}</Link> : <div className={ROW}>{body}</div>}</li>;
                })}
              </ul>
            </>
          ) : (
            <p className={`mt-1 ${QUIET}`}>You are not waiting on anyone.</p>
          )}

          {setup.length > 0 && (
            <>
              <p className="mb-1 mt-5 text-sm font-semibold text-[var(--text-sec)]">What to set up first</p>
              <ul>
                {setup.map((step) => (
                  <li key={step.id}>
                    {step.href && !step.done ? (
                      <Link to={step.href} className={ROW_LINK}>
                        <span className="text-[15px] text-[var(--text-pri)]">{step.label}</span>
                      </Link>
                    ) : (
                      <div className={ROW}>
                        <span className={step.done ? 'text-[var(--text-ter)] line-through' : 'text-[var(--text-pri)]'}>{step.label}</span>
                        {step.done && <span className="shrink-0 text-sm text-[var(--text-ter)]">done</span>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Section>
  );

  const comingUpSection = full && upcoming.length > 0 && (
    <Section id="coming-up" question="Coming up" className="order-4 lg:order-none">
      <ul className="border-b border-[var(--rule)]">
        {upcoming.map((row) => (
          <li key={`${row.kind}:${row.id}`}>
            <Link to={row.href} className={`${ROW_LINK} justify-start`}>
              <span className="w-24 shrink-0 pt-0.5 font-mono text-xs text-[var(--text-ter)]">{row.when.replace(/^[A-Za-z]+, /, '')}</span>
              <span className="text-[15px] leading-snug text-[var(--text-pri)]">{row.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );

  const delegatedToMeSection = full && delegatedToMe.length > 0 && (
    <Section id="delegated-to-me" question="Delegated to me" className="order-4 lg:order-none">
      <DelegationList rows={delegatedToMe} onChanged={delegations.reload} />
    </Section>
  );

  const delegatedByMeSection = full && delegatedByMe.length > 0 && (
    <Section id="you-delegated" question="You delegated" className="order-4 lg:order-none">
      <DelegationList rows={delegatedByMe} onChanged={delegations.reload} />
    </Section>
  );

  const planSection = (
    <div className="order-2 lg:order-none">
      <PlanSection
        data={plan}
        viewerId={userId ?? null}
        canStart={canStart}
        startsWith={helper?.name ?? null}
        itemsWaiting={plan.itemsOnList}
        showBall={teamQ.has('ball')}
        showSinceReview={teamQ.has('sinceReview')}
        loops={loops}
        compact={!full}
      />
    </div>
  );

  const movedSection = full && (
    <Section id="what-moved" question="What moved" className="order-3 lg:order-none">
      {moved.length === 0 && noticesFor !== userId ? (
        <p className={`border-y border-[var(--rule)] py-3.5 ${QUIET}`} aria-busy="true">Checking what reached you…</p>
      ) : moved.length === 0 ? (
        <p className={`border-y border-[var(--rule)] py-3.5 ${QUIET}`}>Nothing new has reached you.</p>
      ) : (
        <ul className="border-b border-[var(--rule)]">
          {moved.map((row) => {
            if (row.kind === 'decision') {
              const body = (
                <span className="min-w-0">
                  <span className="block font-mono text-xs text-[var(--text-ter)]">{row.when} · Decision</span>
                  <span className="mt-0.5 block text-[15px] leading-snug text-[var(--text-pri)]">{row.title}</span>
                  {row.about && <span className="mt-0.5 block text-sm text-[var(--text-ter)]">{row.about}</span>}
                </span>
              );
              return (
                <li key={`decision:${row.id}`}>
                  <Link to={row.href} className={ROW_LINK}>{body}</Link>
                </li>
              );
            }
            // The same news about several things is one row naming each thing.
            const count = 1 + row.alike.length;
            const { listed, points } = changedPoints(row);
            return (
              <li key={`change:${row.id}`} className="border-t border-[var(--rule)] py-3">
                <span className="block font-mono text-xs text-[var(--text-ter)]">
                  {row.when}
                  {count > 1 ? ` · ${count} of these` : ''}
                </span>
                <span className="mt-0.5 block text-[15px] font-medium leading-snug text-[var(--text-pri)]">{row.title}</span>
                {listed ? (
                  <ul className="mt-1.5 flex flex-col gap-1.5">
                    {points.slice(0, 3).map((p) => (
                      <li key={p} title={p} className="line-clamp-2 border-l-2 border-[var(--rule)] pl-2 text-sm leading-snug text-[var(--text-sec)]">{p}</li>
                    ))}
                    {points.length > 3 && <li className="text-sm text-[var(--text-ter)]">and {points.length - 3} more</li>}
                  </ul>
                ) : (
                  points[0] && <span className="mt-0.5 line-clamp-2 block text-sm text-[var(--text-ter)]">{points[0]}</span>
                )}
                {row.earlier.length > 0 && (
                  <span className="mt-0.5 block text-sm text-[var(--text-ter)]">
                    and {row.earlier.length} earlier {row.earlier.length === 1 ? 'change' : 'changes'} to it
                  </span>
                )}
                <span className="mt-2 flex flex-wrap items-center gap-4">
                  {row.href && (
                    <Link to={row.href} className="text-sm font-medium text-[var(--text-pri)] underline underline-offset-4">
                      {count > 1 ? `Review all ${count}` : 'Open'} →
                    </Link>
                  )}
                  <button type="button" onClick={() => void clearMoved(row)} className={MORE_LINK}>
                    {count > 1 ? 'Clear these' : 'Clear'}
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap gap-4">
        <Link to="/prioritization/notifications" className={MORE_LINK}>Everything that reached you</Link>
        {teamQ.has('decided') && <Link to="/prioritization/decisions" className={MORE_LINK}>Every decision</Link>}
      </div>
    </Section>
  );

  const fixSection = full && fixes.length > 0 && (
    <Section id="needs-fixing" question="Needs fixing" tone="fix" className="order-5 lg:order-none">
      <ul className="flex flex-col gap-3">
        {fixes.map((f) => (
          <li key={f.id} className="border border-[var(--rule-bright)] bg-[var(--bg-raised)] p-4">
            <p className="text-[15px] leading-relaxed text-[var(--text-pri)]">{f.text}</p>
            <p className="mt-1.5 text-sm text-[var(--text-ter)]">{f.who}</p>
            <Link to={f.href} className={`mt-2.5 inline-block ${MORE_LINK}`}>Open the cycle</Link>
          </li>
        ))}
      </ul>
    </Section>
  );

  return (
    <div className={full ? '' : 'min-h-screen bg-[var(--bg)]'}>
      <main className={`mx-auto px-4 py-8 sm:px-6 sm:py-10 ${full ? 'max-w-[1400px]' : 'max-w-2xl'}`}>
        <header className="mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-ter)]">
            {workspace?.name ?? 'Priorities'} · {dateLabel}
          </p>
          <h1 className="mt-2 font-heading text-3xl font-semibold text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>
            {greetingFor(today)}
            {firstName ? `, ${firstName}` : ''}.
          </h1>
          {summary !== null && (
            <p className="mt-2.5 text-[17px] leading-relaxed text-[var(--text-sec)]" data-testid="home-summary">
              {needsYou.lead}
              {needsYou.named.map((t, i) => (
                <span key={t.id}>
                  {i === 0 ? ' ' : i === needsYou.named.length - 1 ? ' and ' : ', '}
                  {t.href ? (
                    <Link to={t.href} className="text-[var(--text-pri)] underline decoration-[var(--rule-bright)] underline-offset-4 hover:decoration-[var(--text-pri)]">
                      {t.label}
                    </Link>
                  ) : (
                    <span className="text-[var(--text-pri)]">{t.label}</span>
                  )}
                </span>
              ))}
              {needsYou.named.length > 0 ? '.' : ''}
              {summary ? ` ${summary}` : ''}
            </p>
          )}
          {/* Ask someone for something, with no record behind it (PRD-tasks-asks-and-my-work FR-1). */}
          <div className="mt-4">
            <AskButton home={{ type: 'statement', id: null }} label="Ask someone for something" />
          </div>
        </header>

        {error && (
          <div role="alert" className="mb-6 border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-4 py-3 text-[var(--coral)]">
            {error}
          </div>
        )}

        {full ? (
          // Three columns from 1360px (you · the plan · what moved), two from lg, one below.
          // Below lg the column wrappers dissolve (`contents`) so `order` puts the
          // sections in the phone's order: your turn, the plan, what moved, coming up, needs fixing.
          <div className="flex flex-col gap-10 lg:grid lg:grid-cols-[minmax(0,1.6fr)_minmax(260px,1fr)] lg:items-start lg:gap-8 min-[1360px]:grid-cols-[minmax(250px,0.85fr)_minmax(0,1.75fr)_minmax(260px,1fr)]">
            <div className="contents lg:col-start-1 lg:row-start-1 lg:flex lg:min-w-0 lg:flex-col lg:gap-10">
              {yourTurn}
              {comingUpSection}
              {delegatedToMeSection}
              {delegatedByMeSection}
            </div>
            <div className="contents lg:col-start-1 lg:row-start-2 lg:block lg:min-w-0 min-[1360px]:col-start-2 min-[1360px]:row-start-1">
              {planSection}
            </div>
            <div className="contents lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:flex lg:min-w-0 lg:flex-col lg:gap-10 min-[1360px]:col-start-3 min-[1360px]:row-span-1">
              {movedSection}
              {fixSection}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            {yourTurn}
            {planSection}
            <footer className="border-t border-[var(--rule)] pt-6 text-base text-[var(--text-ter)]">
              <button type="button" onClick={showFullApp} disabled={leaving} {...btnSecondary('min-h-[44px]')}>
                {leaving ? 'One moment…' : 'Show me the full app'}
              </button>
            </footer>
          </div>
        )}
      </main>
    </div>
  );
}
